import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Tooltip, Box, Typography, IconButton } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import BlockIcon from '@mui/icons-material/Block';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';

const generateSlug = (title, year) => {
  if (typeof title !== 'string') {
    return '';
  }
  const cleanedTitle = title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
  return `${cleanedTitle}-${year}`;
};

const CardWrapper = styled.div`
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;
  background-color: white;
  box-shadow: ${props => props.theme.shadows.small};
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

const PosterContainer = styled.div`
  position: relative;
  aspect-ratio: 2/3;
  overflow: hidden;
`;

const Poster = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NoPoster = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f7;
  color: ${props => props.theme.colors.secondary};
`;

const Content = styled.div`
  padding: 12px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h3`
  font-size: 16px;
  margin-bottom: 5px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Year = styled.span`
  font-size: 14px;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 10px;
`;

const RatingContainer = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StarRating = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const Star = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 20px;
  color: ${props => props.active ? '#FFD700' : props.theme.colors.border};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: color 0.2s;
  
  &:hover {
    color: ${props => !props.disabled && (props.hovered || props.active) ? '#FFD700' : props.theme.colors.secondary};
  }
`;

const FeedbackMessage = styled.div`
  font-size: 12px;
  text-align: center;
  color: ${props => props.isError ? props.theme.colors.error : props.theme.colors.success};
  min-height: 18px;
  margin-top: 5px;
`;

const DisplayRating = ({ label, rating, color }) => (
  <Tooltip title={`${label}: ${rating.toFixed(1)} / 5.0`}>
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
      <StarIcon sx={{ color, fontSize: '1rem' }} />
      <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 'bold' }}>
        {rating.toFixed(1)}
      </Typography>
    </Box>
  </Tooltip>
);

function MovieCard({ movie, userRating: initialUserRating, kriticRating, isDisplayOnly = false, onRatingUpdate }) {
  const [userRating, setUserRating] = useState(initialUserRating || 0);
  const [isWannaWatch, setIsWannaWatch] = useState(movie.is_wanna_watch === 1);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', isError: false });
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    setUserRating(initialUserRating || 0);
  }, [initialUserRating]);

  useEffect(() => {
    setIsWannaWatch(movie.is_wanna_watch === 1);
  }, [movie.is_wanna_watch]);

  const handleRateMovie = async (rating) => {
    if (!user || isDisplayOnly) return;

    setIsSubmitting(true);
    setFeedback({ message: 'Saving...', isError: false });

    try {
      await api.rateMovie(user.id, movie.tmdb_id || movie.id, rating, 'tmdb');
      setUserRating(rating);
      if (onRatingUpdate) {
        onRatingUpdate(movie.id, rating, { is_dismissed: false, is_wanna_watch: false });
      }
      setFeedback({ message: 'Rating saved!', isError: false });
      setTimeout(() => setFeedback({ message: '', isError: false }), 3000);
    } catch (err) {
      console.error('Failed to rate movie:', err);
      setFeedback({ message: err.message || 'Failed to save rating', isError: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStarClick = (e, rating) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitting || isDisplayOnly) return;

    const newRating = rating === userRating ? 0 : rating;
    handleRateMovie(newRating);
  };

  const handleDismiss = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || isDisplayOnly || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await api.dismissMovie(user.id, movie.tmdb_id || movie.id, 'tmdb');
      if (onRatingUpdate) {
        onRatingUpdate(movie.id, 0, { is_dismissed: true });
      }
    } catch (err) {
      console.error('Failed to dismiss movie:', err);
      setFeedback({ message: 'Failed to dismiss', isError: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleWannaWatch = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || isDisplayOnly || isSubmitting) return;

    setIsSubmitting(true);
    const newValue = !isWannaWatch;
    try {
      await api.toggleWannaWatch(user.id, movie.tmdb_id || movie.id, newValue, 'tmdb');
      setIsWannaWatch(newValue);

      if (newValue && onRatingUpdate) {
        onRatingUpdate(movie.id, 0, { is_wanna_watch: true });
      }

      setFeedback({ message: newValue ? 'Added to Wanna Watch!' : 'Removed from Wanna Watch', isError: false });
      setTimeout(() => setFeedback({ message: '', isError: false }), 2000);
    } catch (err) {
      console.error('Failed to update Wanna Watch:', err);
      setFeedback({ message: 'Failed to update', isError: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const releaseYear = movie.release_date ? new Date(movie.release_date).getFullYear() : 'Unknown';
  const posterPath = movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null;

  const getDisplayDate = () => {
    if (!movie.release_date) return 'Unknown';

    const releaseDate = new Date(movie.release_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // The API provides dates in 'YYYY-MM-DD' format, which JS's Date() interprets as UTC.
    // We need to adjust for the user's timezone offset to prevent off-by-one errors.
    const userTimezoneOffset = releaseDate.getTimezoneOffset() * 60000;
    const adjustedReleaseDate = new Date(releaseDate.getTime() + userTimezoneOffset);

    if (adjustedReleaseDate > today) {
      return `Coming: ${adjustedReleaseDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return adjustedReleaseDate.getFullYear();
  };

  const movieSlug = movie.title ? generateSlug(movie.title, releaseYear) : '';
  const linkTo = movieSlug ? `/movies/${movieSlug}` : '#';

  return (
    <CardWrapper as={Link} to={linkTo}>
      <Card>
        <PosterContainer>
          {posterPath ? <Poster src={posterPath} alt={movie.title} /> : <NoPoster>No Image</NoPoster>}
        </PosterContainer>

        <Content>
          <Title>{movie.title}</Title>
          <Year>{getDisplayDate()}</Year>

          <RatingContainer>
            {isDisplayOnly ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DisplayRating label="Your Rating" rating={userRating} color="gold" />
                {kriticRating && <DisplayRating label="Kritic Rating" rating={kriticRating} color="#81c784" />}
              </Box>
            ) : (
              <>
                <StarRating>
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star
                      key={star}
                      active={star <= userRating}
                      hovered={star <= hoveredRating}
                      onClick={(e) => handleStarClick(e, star)}
                      onMouseEnter={() => !isDisplayOnly && setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      disabled={isSubmitting || isDisplayOnly}
                    >
                      ★
                    </Star>
                  ))}

                  <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                    <Tooltip title={isWannaWatch ? "Remove from Wanna Watch" : "Wanna Watch"}>
                      <IconButton
                        size="small"
                        onClick={handleToggleWannaWatch}
                        disabled={isSubmitting || userRating > 0}
                        sx={{ color: isWannaWatch ? 'primary.main' : 'text.secondary' }}
                      >
                        {isWannaWatch ? <BookmarkAddedIcon fontSize="small" /> : <WatchLaterIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Dismiss (Never show again)">
                      <IconButton
                        size="small"
                        onClick={handleDismiss}
                        disabled={isSubmitting}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StarRating>
                <FeedbackMessage isError={feedback.isError}>
                  {feedback.message}
                </FeedbackMessage>
              </>
            )}
          </RatingContainer>
        </Content>
      </Card>
    </CardWrapper>
  );
}

export default MovieCard; 